import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm, Controller } from "react-hook-form"
import * as z from "zod"
import { CurrencySelect } from "@/components/ui/currency-select"
import { MoneyInput } from "@/components/ui/money-input"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { enrichTransaction } from "@/lib/http/transaction"
import { ApiError, EnrichTransactionInput, Transaction } from "@/types/api"
import { useState } from "react"
import { ErrorCard } from "@/components/ui/error"
import { CheckCircle, Clock } from "lucide-react"
import { useActiveOrganization, useListOrganizations, useUpdateActiveOrganization } from "@/hooks/useOrganization"

const enrichmentSchema = z.object({
  description: z.string().min(1, "Description is required"),
  amount: z.number({ invalid_type_error: "Amount is required." }),
  currency: z.string().min(1, "Currency is required."),
  date: z.string().optional(),
})

type EnrichmentFormValues = z.infer<typeof enrichmentSchema>

export default function Playground() {
  const [transactionData, setTransactionData] = useState<Transaction | null>(null)
  const [submittedPayload, setSubmittedPayload] = useState<EnrichTransactionInput | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const { refetch: refetchOrganizations } = useListOrganizations()
  const activeOrganization = useActiveOrganization()
  const updateActiveOrganization = useUpdateActiveOrganization()

  const queryClient = useQueryClient()

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<EnrichmentFormValues>({
    resolver: zodResolver(enrichmentSchema),
    defaultValues: {
      currency: "USD",
      description: "APPLE.COM/BILL",
      amount: -29.0,
      date: new Date().toISOString().split("T")[0],
    },
  })

  const mutation = useMutation({
    mutationFn: (data: EnrichTransactionInput) => {
      setFormError(null)
      setTransactionData(null)
      setSubmittedPayload(null)
      return enrichTransaction(data)
    },
    onSuccess: (data, variables) => {
      setTransactionData(data)
      setSubmittedPayload(variables)
      queryClient.invalidateQueries({ queryKey: ["billing", "credit-history", 10, 0] })

      refetchOrganizations().then(({ data }) => {
        if (data && activeOrganization) {
          const updatedOrganization = data.find((org) => org.id === activeOrganization.id)
          if (updatedOrganization) {
            updateActiveOrganization(updatedOrganization)
          }
        }
      })
    },
    onError: (error: ApiError) => {
      setFormError(error?.response?.data?.error || "An unknown error occurred.")
    },
  })

  const onSubmit = (data: EnrichmentFormValues) => {
    const payload: EnrichTransactionInput = {
      ...data,
      amount: data.amount,
      currency: data.currency,
      date: data.date || new Date().toISOString().split("T")[0],
    }
    mutation.mutate(payload)
  }

  return (
    <div className="mt-12 flex flex-1 flex-col items-center">
      <div className="w-full max-w-4xl">
        <div className="grid justify-items-center gap-6">
          <div className="space-y-2 text-center">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">Enrichment Playground</h2>
            <p className="mt-2 max-w-xl text-muted-foreground">
              Enter a description to see it enriched with entity identification, categorization, and more.
            </p>
          </div>
          {/* Input Form */}
          <div className="w-full rounded-lg border bg-card text-card-foreground shadow-sm md:w-fit">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="flex flex-col items-stretch gap-2 p-4 md:flex-row md:items-center">
                <Controller
                  name="currency"
                  control={control}
                  render={({ field }) => <CurrencySelect value={field.value} onChange={field.onChange} />}
                />
                <Input
                  {...register("description")}
                  placeholder="Transaction description e.g., APPLE.COM/BILL"
                  className="h-10 flex-grow border-0 md:h-auto md:w-72"
                />
                <MoneyInput control={control} name="amount" />
                <Input {...register("date")} placeholder="YYYY-MM-DD" className="h-10 border-0 md:h-auto md:w-32" />

                <Button type="submit" className="md:w-auto" size="sm" variant="default" disabled={mutation.isPending}>
                  {mutation.isPending ? "Enriching..." : "Enrich"}
                </Button>
              </div>
            </form>
            {(errors.description || errors.amount || errors.currency) && (
              <div className="px-4 pb-2 text-sm text-red-500">
                {errors.description && <p>{errors.description.message}</p>}
                {errors.amount && <p>{errors.amount.message}</p>}
                {errors.currency && <p>{errors.currency.message}</p>}
              </div>
            )}
          </div>

          {formError && <ErrorCard message={formError} />}

          {/* Results Display */}
          <div className="w-full max-w-2xl">
            {mutation.isPending && (
              <div className="rounded-lg border bg-card p-10 text-center text-card-foreground shadow-sm">
                <p className="text-muted-foreground">Enriching your transaction...</p>
              </div>
            )}

            {transactionData && submittedPayload && (
              <div className="space-y-4">
                {/* Card 1 */}
                <div className="flex items-start justify-between gap-4 rounded-lg border bg-card p-4 text-card-foreground shadow-sm sm:items-center">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <img
                        src={transactionData.entity.logo}
                        alt={transactionData.entity.name}
                        className="h-10 w-10 rounded-lg bg-muted object-contain sm:h-12 sm:w-12"
                      />
                      {transactionData.recurrence.is_recurring && (
                        <div className="absolute -bottom-1 -right-1 rounded-full border-2 border-card bg-blue-500 p-0.5">
                          <Clock className="h-3 w-3 text-white" />
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="whitespace-nowrap text-base font-semibold sm:text-lg">
                        {transactionData.entity.name}
                      </p>
                      <p className="text-sm text-muted-foreground">{transactionData.category.primary}</p>
                    </div>
                  </div>
                  <div className="self-end sm:self-auto">
                    <p className="whitespace-nowrap text-base font-semibold sm:text-lg">
                      {new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: submittedPayload.currency,
                      }).format(submittedPayload.amount)}
                    </p>
                  </div>
                </div>

                {/* Card 2 */}
                <div className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm sm:p-6">
                  <div className="flex flex-col items-start justify-between gap-2 border-b pb-4 sm:flex-row">
                    <div>
                      <h2 className="text-xl font-bold sm:text-2xl">
                        {new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: submittedPayload.currency,
                        }).format(Math.abs(submittedPayload.amount))}
                      </h2>
                      <p className="text-xs text-muted-foreground sm:text-sm">Transaction ID: {transactionData.id}</p>
                    </div>
                    <div className="w-full text-left sm:w-auto sm:text-right">
                      <div className="flex items-center gap-2 text-green-600 sm:justify-end">
                        <CheckCircle className="h-4 w-4" />
                        <span className="text-sm font-semibold">Completed</span>
                      </div>
                      <p className="text-xs text-muted-foreground sm:text-sm">
                        {new Date(`${submittedPayload.date}T${new Date().toTimeString().split(" ")[0]}`).toLocaleString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          }
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="py-6">
                    <div className="flow-root">
                      <dl className="-my-3 text-sm">
                        <h3 className="mb-2 mt-4 text-base font-semibold">Category</h3>
                        <div className="flex items-center justify-between py-3">
                          <dt className="text-muted-foreground">Primary</dt>
                          <dd className="font-medium">{transactionData.category.primary}</dd>
                        </div>
                        <div className="flex items-center justify-between py-3">
                          <dt className="text-muted-foreground">Secondary</dt>
                          <dd className="font-medium">{transactionData.category.secondary.join(", ")}</dd>
                        </div>
                        <div className="flex items-center justify-between py-3">
                          <dt className="text-muted-foreground">Accounting</dt>
                          <dd className="font-medium">{transactionData.category.accounting_category}</dd>
                        </div>
                        <div className="mt-6 flex items-center justify-between py-3">
                          <dt className="text-muted-foreground">Website</dt>
                          <dd className="font-medium">
                            <a
                              href={transactionData.entity.website}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary hover:underline"
                            >
                              {transactionData.entity.website}
                            </a>
                          </dd>
                        </div>
                        <div className="flex items-center justify-between py-3">
                          <dt className="text-muted-foreground">Recurrence</dt>
                          <dd className="font-medium">
                            {transactionData.recurrence.is_recurring
                              ? `Recurring (${transactionData.recurrence.frequency})`
                              : "Not recurring"}
                          </dd>
                        </div>
                      </dl>

                      {transactionData.location && (
                        <div className="mt-8">
                          <div className="grid items-center justify-between py-3">
                            <dt className="mb-2 text-muted-foreground">Address</dt>
                            <dd className="text-sm font-medium">{transactionData.location.address}</dd>
                          </div>
                          {transactionData.location.maps.google_maps_url && (
                            <div className="mt-4 h-96 overflow-hidden rounded-lg border">
                              <iframe
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                loading="lazy"
                                allowFullScreen
                                src={`https://www.google.com/maps/embed/v1/place?key=${
                                  import.meta.env.VITE_GOOGLE_MAPS_API_KEY
                                }&q=${encodeURIComponent(
                                  new URL(transactionData.location.maps.google_maps_url).searchParams.get("query") || ""
                                )}`}
                              ></iframe>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
